// src/api/client.js
import axios from "axios";

let accessToken = "";
let sessionExpiredHandler = null;
let sessionRefreshActivityProvider = null;
let refreshPromise = null;

const api = axios.create({
  baseURL: (process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000").replace(/\/$/, ""),
  withCredentials: true,
});

export function setAccessToken(token) {
  accessToken = token || "";
}

export function getAccessToken() {
  return accessToken;
}

export function clearAccessToken() {
  accessToken = "";
}

export function setSessionExpiredHandler(handler) {
  sessionExpiredHandler = typeof handler === "function" ? handler : null;
}

export function setSessionRefreshActivityProvider(provider) {
  sessionRefreshActivityProvider = typeof provider === "function" ? provider : null;
}

function currentOrgSlug() {
  const stored = JSON.parse(localStorage.getItem("org") || "{}");
  return (
    stored.current ||
    localStorage.getItem("currentOrg") ||
    process.env.REACT_APP_ORG_DEFAULT ||
    "default"
  );
}

function isAuthRefreshRequest(config = {}) {
  const url = String(config.url || "");
  return url.includes("/auth/refresh") || url.includes("/auth/me") || url.includes("/auth/login") || url.includes("/auth/logout");
}

// Always attach org headers and the in-memory access JWT when present.
api.interceptors.request.use((config) => {
  const currentOrg = currentOrgSlug();
  config.headers = {
    ...config.headers,
    "X-Org": currentOrg,
    "X-Organization": currentOrg,
  };

  if (accessToken && !config.skipAuthHeader) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  config.withCredentials = true;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const original = error?.config || {};
    const sessionLike = status === 401 || status === 419;

    if (sessionLike && !original.skipAuthRefresh && !original._retry && !isAuthRefreshRequest(original)) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          const activityHeaders = sessionRefreshActivityProvider?.() ? { "X-User-Activity": "1" } : {};
          refreshPromise = api.post("/auth/refresh", null, {
            headers: activityHeaders,
            skipAuthHeader: true,
            skipAuthRefresh: true,
            skipSessionExpiredModal: true,
          }).finally(() => {
            refreshPromise = null;
          });
        }
        const refreshResponse = await refreshPromise;
        const nextToken = refreshResponse?.data?.access_token || "";
        if (nextToken) setAccessToken(nextToken);
        original.headers = { ...(original.headers || {}) };
        if (nextToken) original.headers.Authorization = `Bearer ${nextToken}`;
        return api(original);
      } catch (refreshError) {
        clearAccessToken();
        if (!original.skipSessionExpiredModal && sessionExpiredHandler) sessionExpiredHandler();
        return Promise.reject(refreshError);
      }
    }

    if (sessionLike && !original.skipSessionExpiredModal && !isAuthRefreshRequest(original)) {
      clearAccessToken();
      if (sessionExpiredHandler) sessionExpiredHandler();
    }

    return Promise.reject(error);
  }
);

export async function postForm(url, kv = {}) {
  const form = new FormData();
  Object.entries(kv).forEach(([k, v]) => {
    if (v !== undefined && v !== null) form.append(k, v);
  });
  return api.post(url, form);
}

export default api;
